import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	addCalendarDays,
	latestClosedBusinessDay,
	resolvePeriod,
	type PeriodSelection,
} from '@ozon-ai/periods';
import type { PeriodPreset } from '@ozon-ai/user/contracts';
import { formatPeriodRange } from './format-period';

export type PeriodPickerMode = 'week' | 'month' | 'custom' | 'preset';
export type PeriodPickerToggle = 'week' | 'month' | 'custom' | 'presets';

export function periodSelectionKey(selection: PeriodSelection | null): string {
	return selection ? JSON.stringify(selection) : '';
}

export function closedMonthOptions(anchorDay: string, count = 18): string[] {
	const [year, month] = anchorDay.split('-').map(Number);
	const months: string[] = [];
	let cursorYear = year;
	let cursorMonth = month;
	for (let index = 0; index < count; index += 1) {
		months.push(`${cursorYear}-${String(cursorMonth).padStart(2, '0')}`);
		cursorMonth -= 1;
		if (cursorMonth === 0) {
			cursorMonth = 12;
			cursorYear -= 1;
		}
	}
	return months;
}

function monthRange(yearMonth: string, anchorDay: string): { from: string; to: string } {
	const [year, month] = yearMonth.split('-').map(Number);
	const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
	const from = `${yearMonth}-01`;
	let to = `${yearMonth}-${String(last).padStart(2, '0')}`;
	if (to > anchorDay) to = anchorDay;
	if (from > to) return { from: anchorDay, to: anchorDay };
	return { from, to };
}

function weekRange(anchorDay: string): {from: string; to: string} {
	return {from: addCalendarDays(anchorDay, -6), to: anchorDay};
}

export type PeriodPickerCommitted = {
	toggle: PeriodPickerToggle;
	month: string;
	customFrom: string;
	customTo: string;
	presetId: string | null;
};

function hydrateCommitted(
	committed: PeriodPickerCommitted,
	latestDay: string,
	months: string[],
): PeriodPickerCommitted {
	const base = initialCommitted(latestDay, months, undefined, []);
	return {
		...base,
		...committed,
		month: committed.month || base.month,
		customFrom: committed.customFrom || base.customFrom,
		customTo: committed.customTo || base.customTo,
	};
}

function initialCommitted(
	latestDay: string,
	months: string[],
	primaryPresetId: string | undefined,
	presets: PeriodPreset[],
): PeriodPickerCommitted {
	const week = weekRange(latestDay);
	const base = {
		month: months[0] ?? '',
		customFrom: week.from,
		customTo: week.to,
		presetId: null as string | null,
	};
	if (primaryPresetId && presets.some((preset) => preset.id === primaryPresetId)) {
		return {...base, toggle: 'presets', presetId: primaryPresetId};
	}
	return {...base, toggle: 'week'};
}

function committedToSelection(
	committed: PeriodPickerCommitted,
	latestDay: string,
	presets: PeriodPreset[],
): PeriodSelection | null {
	if (committed.toggle === 'week') return {type: 'range', ...weekRange(latestDay)};
	if (committed.toggle === 'month' && committed.month) {
		return {type: 'range', ...monthRange(committed.month, latestDay)};
	}
	if (committed.toggle === 'custom') {
		const {customFrom, customTo} = committed;
		if (!customFrom || !customTo || customFrom > customTo) return null;
		return {type: 'range', from: customFrom, to: customTo};
	}
	if (committed.toggle === 'presets' && committed.presetId) {
		const preset = presets.find((item) => item.id === committed.presetId);
		if (!preset) return null;
		return {type: 'preset', preset: preset.definition};
	}
	return null;
}

export function usePeriodPicker(options: {
	presets: PeriodPreset[];
	primaryPresetId?: string;
	presetsReady?: boolean;
	initial?: PeriodPickerCommitted | null;
}) {
	const {presets, primaryPresetId, presetsReady = true, initial = null} = options;
	const latestDay = useMemo(() => latestClosedBusinessDay(), []);
	const months = useMemo(() => closedMonthOptions(latestDay), [latestDay]);
	const userAdjusted = useRef(initial != null);

	const [committed, setCommitted] = useState<PeriodPickerCommitted | null>(() =>
		initial ? hydrateCommitted(initial, latestDay, months) : null,
	);

	useEffect(() => {
		if (!presetsReady || userAdjusted.current) return;
		setCommitted((prev) =>
			prev ?? initialCommitted(latestDay, months, primaryPresetId, presets),
		);
	}, [presetsReady, presets, primaryPresetId, latestDay, months]);

	const patchCommitted = useCallback(
		(patch: Partial<PeriodPickerCommitted> & Pick<PeriodPickerCommitted, 'toggle'>) => {
			userAdjusted.current = true;
			setCommitted((prev) => {
				const base = prev ?? initialCommitted(latestDay, months, undefined, []);
				return {...base, ...patch};
			});
		},
		[latestDay, months],
	);

	const applyWeek = useCallback(() => {
		patchCommitted({toggle: 'week', presetId: null});
	}, [patchCommitted]);

	const applyMonth = useCallback(
		(yearMonth: string) => {
			patchCommitted({toggle: 'month', month: yearMonth, presetId: null});
		},
		[patchCommitted],
	);

	const applyCustom = useCallback(
		(from: string, to: string) => {
			patchCommitted({
				toggle: 'custom',
				customFrom: from,
				customTo: to,
				presetId: null,
			});
		},
		[patchCommitted],
	);

	const applyPreset = useCallback(
		(presetId: string) => {
			patchCommitted({toggle: 'presets', presetId});
		},
		[patchCommitted],
	);

	const selection = useMemo(() => {
		if (!committed) return null;
		return committedToSelection(committed, latestDay, presets);
	}, [committed, latestDay, presets]);

	const localError =
		committed &&
		committed.toggle === 'custom' &&
		committed.customFrom &&
		committed.customTo &&
		committed.customFrom > committed.customTo
			? 'Дата «С» не может быть позже даты «По»'
			: null;

	let resolvedLabel = '';
	if (selection) {
		try {
			const range = resolvePeriod(selection, latestDay);
			resolvedLabel = formatPeriodRange(range.from, range.to);
		} catch (error) {
			resolvedLabel = error instanceof Error ? error.message : '';
		}
	}

	const presetsButtonLabel = useMemo(() => {
		if (committed?.toggle === 'presets' && committed.presetId) {
			return presets.find((preset) => preset.id === committed.presetId)?.name ?? 'Сохранённые';
		}
		const primary = presets.find((preset) => preset.id === primaryPresetId);
		return primary?.name ?? 'Сохранённые';
	}, [committed, presets, primaryPresetId]);

	const ready = presetsReady && committed !== null;

	return {
		latestDay,
		months,
		presets,
		primaryPresetId,
		committed,
		ready,
		selection,
		localError,
		resolvedLabel,
		presetsButtonLabel,
		applyWeek,
		applyMonth,
		applyCustom,
		applyPreset,
	};
}

export type PeriodPickerState = ReturnType<typeof usePeriodPicker>;
